import { Button, Chip, Grid, Tooltip } from "@mui/material";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import { useMemo } from "react";
import CheckEligibilityModal from "../pages/app/provider/patient/patient-list/patient-chart-new/demographics/eligibility/CheckEligibilityModal";
import EligibilityResponseModal from "../pages/app/provider/patient/patient-list/patient-chart-new/demographics/eligibility/eligibilityResponseModal";
import { useEligibilityVerification } from "../hooks/use-eligibility-verification";
import Flex from "./Flex";
import Text from "./Text";

interface EligibilityVerificationProps {
  /** Patient whose eligibility records should be fetched. */
  patientId?: string;
  /**
   * Primary insurance of the patient; eligibility checks require a valid one.
   * Also used to prefill the Insurance field of the Check Eligibility / Recheck
   * modal so the user doesn't have to re-select it.
   */
  primaryIns?: {
    uuid?: string;
    insuranceId?: string;
    insurancePayer?: { payerName?: string };
  } | null;
  /** Provider on the appointment, used to prefill the modal's Provider field. */
  providerId?: string;
  /** Display name of the provider, used as the prefilled Provider option label. */
  providerName?: string;
}

/**
 * Renders the "Eligibility Verification" row of the appointment view:
 * the verified chip / view-report / recheck actions and the check-eligibility
 * trigger, plus the related modals. All state and handlers come from
 * {@link useEligibilityVerification}.
 *
 * @author rajkumar rathod
 */
const EligibilityVerification: React.FC<EligibilityVerificationProps> = ({
  patientId,
  primaryIns,
  providerId,
  providerName,
}) => {
  const {
    hasViewEligibility,
    latestEligibilityRecord,
    canCheckEligibility,
    checkEligibilityDisclosure,
    eligibilityResponseDisclosure,
    eligibilityReportDisclosure,
    openCheckEligibility,
    openEligibilityResponse,
    handleEligibilityChecked,
  } = useEligibilityVerification({ patientId, primaryIns });

  // Prefill the Check Eligibility / Recheck modal's Insurance field with the
  // patient's primary insurance so it auto-populates instead of starting empty.
  const prefillEligibilityInsurance = useMemo(() => {
    if (!primaryIns?.uuid || !primaryIns?.insuranceId) return undefined;
    return {
      value: primaryIns.uuid,
      label: primaryIns.insurancePayer?.payerName ?? "",
      insuranceId: primaryIns.insuranceId,
    };
  }, [primaryIns]);

  // Prefill the modal's Provider field with the appointment's provider.
  const prefillEligibilityProvider = useMemo(() => {
    if (!providerId) return undefined;
    return {
      value: providerId,
      label: providerName ?? "",
    };
  }, [providerId, providerName]);

  if (!hasViewEligibility) {
    return null;
  }

  return (
    <Grid container mt={1.6}>
      <Grid item xs={3.4}>
        <Text fontWeight={"medium"} color={"#1A1A1A80"}>
          Eligibility Verification
        </Text>
      </Grid>

      <Grid item paddingLeft={"1rem"} xs={8.5}>
        {checkEligibilityDisclosure.opened && (
          <CheckEligibilityModal
            disclosureHost={checkEligibilityDisclosure}
            eligibilityReportDisclosure={eligibilityReportDisclosure}
            onEligibilityChecked={handleEligibilityChecked}
            prefillIns={prefillEligibilityInsurance}
            prefillProvider={prefillEligibilityProvider}
          />
        )}
        {eligibilityResponseDisclosure.opened && (
          <EligibilityResponseModal
            disclosureHost={eligibilityResponseDisclosure}
          />
        )}

        <Flex alignItems="center" gap={1.5} mt={0.5}>
          {latestEligibilityRecord ? (
            <>
              <Chip
                label="Verified"
                size="small"
                sx={{
                  bgcolor: "#e8f5e9",
                  color: "#2e7d32",
                }}
              />
              <Text
                sx={{
                  color: "primary.main",
                  cursor: "pointer",
                  textDecoration: "underline",
                }}
                onClick={() => openEligibilityResponse()}
              >
                View Report
              </Text>

              <Tooltip title="">
                <Flex
                  alignItems="center"
                  gap={0.5}
                  sx={{
                    cursor: canCheckEligibility ? "pointer" : "default",
                    opacity: canCheckEligibility ? 1 : 0.5,
                  }}
                  onClick={openCheckEligibility}
                >
                  <RefreshOutlinedIcon
                    sx={{
                      fontSize: 14,
                      color: "primary.main",
                    }}
                  />
                  <Text
                    sx={{
                      color: "primary.main",
                      textDecoration: "underline",
                      fontWeight: 500,
                    }}
                  >
                    Recheck
                  </Text>
                </Flex>
              </Tooltip>
            </>
          ) : (
            <Button
              variant="text"
              color="primary"
              disabled={!canCheckEligibility}
              onClick={openCheckEligibility}
              sx={{ textTransform: "none", minWidth: 0, p: 0 }}
            >
              Check Eligibility
            </Button>
          )}
        </Flex>
      </Grid>
    </Grid>
  );
};

export default EligibilityVerification;
